import { isInvalidFileName, isInvalidFloat, isInvalidString } from "src/validation/stringValidation";
import {
  isInvalidPurchaseOrderNumber,
  isInvalidTrackingNumber,
  isInvalidQuantity
} from "src/validation/stringValidation";
import { isInvalidShipmentCost, isInvalidImportDutyCost } from "src/validation/stringValidation";
import { isInvalidShipmentUnitType } from "src/helpers/shipmentHelper";
import { isInvalidFileSize, isInvalidFileType } from "src/helpers/fileHelper";
import { FormValidator } from "../formValidator";
import { ValidationError, ValidationErrors } from "src/errors";
import { UpdatePurchaseOrderShipmentDetailsFormDataKey, ShipmentUnitInputField } from "src/types";

class UpdatePurchaseOrderShipmentDetailsFormValidator extends FormValidator {
  private static validatePurchaseOrderNumber(value: unknown): ValidationError<"purchaseOrderId"> | undefined {
    return isInvalidPurchaseOrderNumber(value)
      ? new ValidationError("Enter a valid purchase order number (e.g., 'PO-2055')", "purchaseOrderId")
      : undefined;
  }

  private static validateTrackingNumber(value: unknown): ValidationError<"trackingNo"> | undefined {
    return value && isInvalidTrackingNumber(value)
      ? new ValidationError("Enter a valid tracking number", "trackingNo")
      : undefined;
  }

  private static validateCarrier(value: unknown): ValidationError<"carrier"> | undefined {
    return value && isInvalidString(value, 2, 30) ? new ValidationError("Enter a valid carrier", "carrier") : undefined;
  }

  private static validatePackingListFiles(value: unknown): ValidationError<"packingListFiles"> | undefined {
    if (!Array.isArray(value) || !value.length) {
      return;
    }

    if (value.length > 10) {
      return new ValidationError("Can only upload up to 10 files", "packingListFiles");
    }

    for (const arrayValue of value as File[]) {
      if (
        isInvalidFileType(arrayValue, [
          "application/pdf",
          "application/msword",
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          "application/vnd.openxmlformats-officedocument.presentationml.presentation",
          "image/png",
          "image/jpeg",
          "image/webp",
          "text/csv",
          "text/plain"
        ])
      ) {
        return new ValidationError("File type is invalid", "packingListFiles");
      }

      if (isInvalidFileSize(arrayValue)) {
        return new ValidationError("File size is invalid", "packingListFiles");
      }

      if (isInvalidFileName(arrayValue.name)) {
        return new ValidationError("File name is invalid", "packingListFiles");
      }
    }
    return undefined;
  }

  private static validateShipmentCost(value: unknown): ValidationError<"shipmentCost"> | undefined {
    return value && isInvalidShipmentCost(value?.toString())
      ? new ValidationError("Enter a valid shipment cost (e.g., '50.05')", "shipmentCost")
      : undefined;
  }

  private static validateImportDutyCost(value: unknown): ValidationError<"importDutyCost"> | undefined {
    return value && isInvalidImportDutyCost(value?.toString())
      ? new ValidationError("Enter a valid import duty cost (e.g., '50.05')", "importDutyCost")
      : undefined;
  }

  private static validateShipmentUnits(value: unknown): ValidationError<"shipmentUnits"> | undefined {
    if (!Array.isArray(value) || !value.length) {
      return;
    }

    for (const arrayValue of value) {
      const { type, quantity, weight, length, width, height } = arrayValue as ShipmentUnitInputField;

      if (isInvalidFloat(height, 1, 100000)) {
        return new ValidationError("Shipment unit height is invalid", "shipmentUnits");
      }

      if (isInvalidFloat(width, 1, 100000)) {
        return new ValidationError("Shipment unit width is invalid", "shipmentUnits");
      }

      if (isInvalidFloat(length, 1, 100000)) {
        return new ValidationError("Shipment unit length is invalid", "shipmentUnits");
      }

      if (isInvalidFloat(weight, 1, 100000)) {
        return new ValidationError("Shipment unit weight is invalid", "shipmentUnits");
      }

      if (isInvalidQuantity(quantity, 1, 100000)) {
        return new ValidationError("Shipment unit quantity is invalid", "shipmentUnits");
      }

      if (isInvalidShipmentUnitType(type)) {
        return new ValidationError("Shipment unit type is invalid (must be: 'Pallets' or 'Boxes')", "shipmentUnits");
      }
    }

    return undefined;
  }

  private static validateAdditionalInfo(value: unknown): ValidationError<"additionalInfo"> | undefined {
    return value && isInvalidString(value, 0, 1200)
      ? new ValidationError("Additional information is too long or invalid", "additionalInfo")
      : undefined;
  }

  private static readonly validationStrategies: Record<
    UpdatePurchaseOrderShipmentDetailsFormDataKey,
    (inputValue: unknown) => ValidationError<UpdatePurchaseOrderShipmentDetailsFormDataKey> | undefined
  > = {
    purchaseOrderId: UpdatePurchaseOrderShipmentDetailsFormValidator.validatePurchaseOrderNumber,
    trackingNo: UpdatePurchaseOrderShipmentDetailsFormValidator.validateTrackingNumber,
    carrier: UpdatePurchaseOrderShipmentDetailsFormValidator.validateCarrier,
    shipmentCost: UpdatePurchaseOrderShipmentDetailsFormValidator.validateShipmentCost,
    importDutyCost: UpdatePurchaseOrderShipmentDetailsFormValidator.validateImportDutyCost,
    packingListFiles: UpdatePurchaseOrderShipmentDetailsFormValidator.validatePackingListFiles,
    shipmentUnits: UpdatePurchaseOrderShipmentDetailsFormValidator.validateShipmentUnits,
    additionalInfo: UpdatePurchaseOrderShipmentDetailsFormValidator.validateAdditionalInfo
  };

  static validatePurchaseOrderFormData<T extends UpdatePurchaseOrderShipmentDetailsFormDataKey>(
    formData: Partial<Record<T, unknown>>
  ): ValidationErrors<T> {
    return super.validateFormData(
      formData,
      UpdatePurchaseOrderShipmentDetailsFormValidator.validationStrategies
    ) as ValidationErrors<T>;
  }
}

export { UpdatePurchaseOrderShipmentDetailsFormValidator };
